import { ClearanceRequestStatus, IPagination, IShippingTypeLocation, ReturnRequestStatus } from "models";
import { GetSkuRelationsParams } from "models/setcode-config";
import { t } from "utils/i18n";
import fileConstants from "./file";
import stockImbalanceConstants from "./stock-imbalance";

const IAM_SCOPES = [
  "openid",
  "profile",
  "read:permissions",
  // You can add more scopes (granted services) here
  "accreport",
  "apl",
  "as",
  "dca",
  "sellers",
  "staff-bff",
  "ticket-bff",
  "wms",
  "catalog",
  "tenant:management",
  "us",
  "ons-bff",
  "uns-bff-api",
];

const PAGE_SIZE_OPTIONS = ["10", "20", "50"];
const MAX_PAGE_SIZE = 200;
const DEFAULT_PAGINATION = {
  current: 1,
  pageSize: 10,
  pageSizeOptions: PAGE_SIZE_OPTIONS,
};

const DEFAULT_SIZE_CHANGER = {
  showSizeChanger: true,
  pageSizeOptions: PAGE_SIZE_OPTIONS,
  locale: {
    items_per_page: t("RecordPerPage"),
  },
};

const DEFAULT_FILTERS = {
  MANUAL_INVENTORY: {
    TRACKING: {
      page: DEFAULT_PAGINATION.current,
      pageSize: DEFAULT_PAGINATION.pageSize,
      keyword: undefined,
    },
    FILE_UPLOAD_INVENTORY_HISTORY: {
      offset: 0,
      limit: DEFAULT_PAGINATION.pageSize,
      type: fileConstants.FILE_TYPE.INVENTORY,
    },
    FILE_UPLOAD_WAREHOUSE_HISTORY: {
      type: fileConstants.FILE_TYPE.WAREHOUSE,
    },
    FILE_UPLOAD_SITE_HISTORY: {
      type: fileConstants.FILE_TYPE.SITE,
    },
    LIST_WAREHOUSE: {
      page: DEFAULT_PAGINATION.current,
      pageSize: DEFAULT_PAGINATION.pageSize,
    },
    LIST_SITE: {
      page: DEFAULT_PAGINATION.current,
      pageSize: DEFAULT_PAGINATION.pageSize,
    },
  },
  PLATFORM: {
    page: DEFAULT_PAGINATION.current,
    pageSize: MAX_PAGE_SIZE,
  },
  TERMINAL: {
    page: DEFAULT_PAGINATION.current,
    pageSize: MAX_PAGE_SIZE,
  },
  RETURN_REQUEST: {
    page: DEFAULT_PAGINATION.current,
    pageSize: DEFAULT_PAGINATION.pageSize,
    statuses: [
      ReturnRequestStatus.waitApproval,
      ReturnRequestStatus.waitApprovalImported,
      ReturnRequestStatus.waitApprovalCancelled,
    ],
    orderIds: undefined,
    sellerId: undefined,
    requestDate: undefined,
    createdByEmail: undefined,
  },
  RETURN_REASON: {
    isStaffOnly: false,
    status: undefined,
    page: DEFAULT_PAGINATION.current,
    pageSize: DEFAULT_PAGINATION.pageSize,
  },
  INVENTORY_ALERT: {
    page: DEFAULT_PAGINATION.current,
    pageSize: DEFAULT_PAGINATION.pageSize,
    status: `${ClearanceRequestStatus.available},${ClearanceRequestStatus.expired}`,
    originalSku: undefined,
  },
  PURCHASE_ORDERS: {
    limit: 10,
    offset: 0,
  },
  QUOTATION_LIST: {
    page: DEFAULT_PAGINATION.current,
    pageSize: DEFAULT_PAGINATION.pageSize,
  },
  STOCK_IMBALANCE: {
    offset: 0,
    limit: stockImbalanceConstants.SKU_LIMIT,
  },
  BRAND: {
    page: DEFAULT_PAGINATION.current,
    pageSize: DEFAULT_PAGINATION.pageSize,
  },
};

const BYPASSED_NOT_FOUND_ERROR_URLS = [/delivery\/\d+\/cancel-request$/, /loyalty-computation-config/];

const STATUS_CODES = {
  IAM_CONFIG_NOT_FOUND: 40411,
};

const NOT_AVAILABLE = "N/A";
const NOT_FOUND = "Not Found";

export const TAKASHIMAYA_DOMAINS = [
  "https://admin.takashimaya-vn.com", //PROD
  "https://tkv-staff.stag.teko.vn", //STAG
  "https://tkv-staff.dev.teko.vn", //DEV
];

export const VIEON_DOMAINS = [
  "https://agency-admin.vieon.vn", //PROD
  "https://admin-vieon.stag.teko.vn", //STAG
  "https://admin-vieon.dev.teko.vn", //DEV
];

const X_SELLER_ID = "x-seller-id";
const X_PLATFORM_ID = "x-platform-id";

const VND_CURRENCY = {
  precision: 3,
};

const MIDDLE_DOT_SYMBOL = "·";

const DISCOUNT_SKU_FOR_PHONGVU = "1600103";
const STATUS_CODES_ACTIVE_BFF_CATALOG = "active,processing,pending_approval,reject";

const PURCHASE_INVOICE_UNIT_PRICE_MAX_DIFF = 500;

const DURATION_IN_SECONDS = {
  ONE_SECOND: 1,
};

const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,5})+$/;
const PHONE_REGEX = /^((\+84\s?|\+84\s?0|0)[3|5|7|8|9])+([0-9]{8})$/;
const TELEPHONE_REGEX = /^02\d{8,9}$/;
const TAX_CODE_REGEX_V1 = /^\d{10}(-\d{3})?$/; // Tax code format: 10 digits (1234567890) or 13 digits (1234567890-000)
const TAX_CODE_REGEX_V2 = /^(\d{10}(-\d{3})?|\d{12})$/; // Tax code format: 10 digits (1234567890) or 13 digits (1234567890-000) or 12 digits(123456789000)
const PASSPORT_NUMBER_REGEX = /^[A-Za-z0-9]{1,20}$/; // Passport number format: Up to 20 alphanumeric characters, no spaces, no special characters
const CITIZEN_ID_NUMBER_REGEX = /^(\d{9}|\d{12})$/; // Citizen ID number. Must be a 9 or 12-digit string as per Vietnam's regulations
const STATE_BUDGET_UNIT_CODE_REGEX = /^[12378]\d{6}$/; // Code of the state budget-related unit. Must be a 7-digit string as per Vietnam's regulations. The first digit must be one of '1', '2', '3', '7', or '8'.
// [PI-9999][OMNI-2093] Some state budget-related units use codes starting with '9', and the E-invoice
// partner (Viettel) does not enforce the leading-digit rule either — drop it, keep only the 7-digit length.
const STATE_BUDGET_UNIT_CODE_REGEX_V2 = /^\d{7}$/;

const START_EDIT_MESSAGE = "start edit";
const END_EDIT_MESSAGE = "stop edit";

const START_DOWNLOAD_MESSAGE = "start download";
const END_DOWNLOAD_MESSAGE = "end download";

const LOGOUT_MESSAGE = "logout";

const MAX_TEXT_INPUT_LENGTH = 255;

const MAX_CUSTOMER_NAME_LENGTH = 100;
const MAX_CUSTOMER_NAME_LENGTH_V2 = 400;

const DEFAULT_LOCATIONS_VALUE: IShippingTypeLocation[] = [
  {
    locationId: "0",
    locationName: "Toàn quốc",
  },
];

const ENTERPRISE_WAREHOUSE_PROVIDER = "-1";

enum PROMISE_STATUS {
  Fulfilled = "fulfilled",
  Rejected = "rejected",
}

const SERVICE_QUANTITY = 1;
const ZERO_QUANTITY = 0;

const DELAY_TIME_BEFORE_DIRECT_TO_EXPORT_HISTORY = 500;
const DELAY_TIME = 500;
const DELAY_TIME_BEFORE_DIRECT_TO_LIST_POR = 2000;
const DELAY_TIME_BEFORE_REFRESH_MANUAL_INVENTORY = 1000;

const DEFAULT_NUMBER_DISPLAY_FORMAT = {
  thousandSeparator: ".",
  decimalSeparator: ",",
};
const OM_DEFAULT_NUMBER_DISPLAY_FORMAT = {
  thousandSeparator: ",",
  decimalSeparator: ".",
};
const DEFAULT_DECIMAL_SEPARATOR = ".";

const THOUSAND_FORMAT_REGEX = new RegExp(/\B(?=(\d{3})+(?!\d))/g);
const INTEGER_OR_FLOAT_REGEX = new RegExp(/^-?\d+(\.\d+)?$/);

const URL_SEARCH_PARAMS_REGEX = /\?.*$/g;

const ALL_TERMINAL = 0;

const API_RESPONSE_TIME_BUCKETS = [
  {
    value: 200,
    label: "0 - 200",
  },
  {
    value: 500,
    label: "200 - 500",
  },
  {
    value: 800,
    label: "500 - 800",
  },
  {
    value: 1000,
    label: "800 - 1000",
  },
  {
    value: 1500,
    label: "1000 - 1500",
  },
  {
    value: 2000,
    label: "1500 - 2000",
  },
  {
    value: 3000,
    label: "2000 - 3000",
  },
  {
    value: 4000,
    label: "3000 - 4000",
  },
  {
    value: 5000,
    label: "4000 - 5000",
  },
  {
    value: 8000,
    label: "5000 - 8000",
  },
  {
    value: 10000,
    label: "8000 - 10000",
  },
  {
    value: 11000,
    label: "> 10000",
  },
];

const DEFAULT_PAGINATION_TYPE_OFFSET = {
  offset: 0,
  limit: 10,
};

const DEFAULT_PAGINATION_TYPE_OFFSET_STRING = {
  offset: "",
  limit: 10,
};

const LAST_PAGE_OFFSET = "-1";

const DEFAULT_PAGINATION_TYPE_PAGESIZE = {
  page: 1,
  pageSize: 10,
};

const MAX_SELECTED_ITEMS_10 = 10;

const DEFAULT_FILTER: GetSkuRelationsParams = {
  page: 1,
  pageSize: 10,
};

const PAGINATION: IPagination = {
  current: 1,
  pageSize: 10,
  total: 0,
};

const FIRST_PAGE = 1;

const MAX_PERIOD_31_DAYS = 31;

// CODE128 encodes an alphanumeric value as is, so a scan gives back the exact value
const BARCODE_FORMAT = "CODE128";

const TK_STATUS_COLOR = {
  WAITING: "warning",
  WAITING_SECONDARY: "volcano",
  PROCESSING: "blue",
  PROCESSING_SECONDARY: "geekblue",
  SUCCESS: "success",
  ERROR: "error",
  DEFAULT: "default",
};

export default {
  ALL: "all",
  APP_TITLE: "HCM2 ERP Staff",
  BARCODE_FORMAT,
  BYPASSED_NOT_FOUND_ERROR_URLS,
  DEBOUNCE_WAITING_TIME: 500, // in milliseconds
  DEFAULT_FILTERS,
  DEFAULT_LOCATIONS_VALUE,
  DEFAULT_PAGINATION,
  DEFAULT_SIZE_CHANGER,
  DISCOUNT_SKU_FOR_PHONGVU, // hard code for Phong Vũ to discount in Purchase Invoice
  STATUS_CODES_ACTIVE_BFF_CATALOG,
  POLLING_INTERVAL: 1000, // milliseconds
  DOWNLOAD_POLLING_INTERVAL: 10000, // in milliseconds
  DURATION_IN_SECONDS,
  EMAIL_REGEX,
  END_DOWNLOAD_MESSAGE,
  END_EDIT_MESSAGE,
  ENTERPRISE_WAREHOUSE_PROVIDER,
  IAM_SCOPES,
  LOGOUT_MESSAGE,
  MAX_PAGE_SIZE,
  MAX_TEXT_INPUT_LENGTH,
  MAX_CUSTOMER_NAME_LENGTH,
  MAX_CUSTOMER_NAME_LENGTH_V2,
  MIDDLE_DOT_SYMBOL,
  NOT_AVAILABLE,
  NOT_FOUND,
  PAGE_SIZE_OPTIONS,
  PHONE_REGEX,
  TELEPHONE_REGEX,
  PROMISE_STATUS,
  PURCHASE_INVOICE_UNIT_PRICE_MAX_DIFF,
  SERVICE_QUANTITY,
  ZERO_QUANTITY,
  START_DOWNLOAD_MESSAGE,
  START_EDIT_MESSAGE,
  STATUS_CODES,
  TABLET_WIDTH: 768,
  TAKASHIMAYA_DOMAINS, // hardcode for Takashimaya E-Invoice,
  TAX_CODE_REGEX_V1,
  TAX_CODE_REGEX_V2,
  VND_CURRENCY,
  X_SELLER_ID,
  X_PLATFORM_ID,
  DELAY_TIME_BEFORE_DIRECT_TO_EXPORT_HISTORY,
  DELAY_TIME,
  DELAY_TIME_BEFORE_REFRESH_MANUAL_INVENTORY,
  ONE_MIN_IN_MS: 60000,
  DELAY_TIME_BEFORE_DIRECT_TO_LIST_POR,
  DEFAULT_NUMBER_DISPLAY_FORMAT,
  OM_DEFAULT_NUMBER_DISPLAY_FORMAT,
  DEFAULT_DECIMAL_SEPARATOR,
  THOUSAND_FORMAT_REGEX,
  DEFAULT_PRECISION: 4,
  INTEGER_OR_FLOAT_REGEX,
  URL_SEARCH_PARAMS_REGEX,
  UNDEFINED_URL: "undefined_url",
  API_RESPONSE_TIME_BUCKETS,
  DEFAULT_PAGE: 1,
  ALL_TERMINAL,
  DEFAULT_PAGINATION_TYPE_OFFSET,
  DEFAULT_PAGINATION_TYPE_OFFSET_STRING,
  LAST_PAGE_OFFSET,
  CITIZEN_ID_NUMBER_REGEX,
  STATE_BUDGET_UNIT_CODE_REGEX,
  STATE_BUDGET_UNIT_CODE_REGEX_V2,
  PASSPORT_NUMBER_REGEX,
  ALL_PERMISSION_ID: 0,
  DEFAULT_MAX_TAG_COUNT: 2,
  DEFAULT_PAGINATION_TYPE_PAGESIZE,
  MAX_SELECTED_ITEMS_10,
  DEFAULT_FILTER,
  PAGINATION,
  FIRST_PAGE,
  MAX_PERIOD_31_DAYS,
  TK_STATUS_COLOR,
};
